import React, { forwardRef } from 'react';
import { ReactThreeFiber, useThree } from 'react-three-fiber';
import { Text } from 'drei';
import { fontFamilyNormal, textAlignCenter, textAlignLeft, textDirection } from '../../../config/ui';
import { LOCALE } from '../../../config/secrets';

type LinkType = {
  link:string;
  text:string;
  spaces: number;
  color: string;
  underline_text: string;
  underline_color: string;
}

type TTextProps = {
  value: string;
  highlightValue?: string;
  linkValue?: string;
  links: LinkType[];
  activeSlideIndex: number;
  lastSlideIndex: number
};

const TitleText = forwardRef(({ value, highlightValue }: TTextProps, ref) => {
  const fontSize = 0.049;
  const anchorX = 'center';
  const anchorY = 'middle';
  const color = '#ffffff';
  const position: ReactThreeFiber.Vector3 = [0, 0, 0.001];

  return (
    <group position={position}>
      <Text
        renderOrder={2}
        color={color}
        font={fontFamilyNormal}
        fontSize={fontSize}
        maxWidth={0.48}
        lineHeight={1.2}
        textAlign={textAlignCenter}
        anchorX={anchorX}
        anchorY={anchorY}>
        {value}
      </Text>
      <Text
        ref={ref}
        renderOrder={3}
        color="#ffffff"
        font={fontFamilyNormal}
        fontSize={fontSize}
        maxWidth={0.48}
        lineHeight={1.2}
        textAlign={textAlignCenter}
        anchorX={anchorX}
        anchorY={anchorY}>
        {highlightValue}
      </Text>
    </group>
  );
});

const BigText = forwardRef(({ value, highlightValue, linkValue }: TTextProps, ref) => {
  const fontSize = 0.027;
  const color = '#ffffff';
  const positionSign = LOCALE === "/ar" ? 1 : -1;
  const anchorX = positionSign === 1 ? 'right' : "left";
  const anchorY = 'top';
  const position: ReactThreeFiber.Vector3 = [positionSign * 0.24, 0.2, 0.001];

  return (
    <group position={position}>
      <Text
        renderOrder={2}
        color={color}
        font={fontFamilyNormal}
        fontSize={fontSize}
        maxWidth={0.48}
        lineHeight={1.2}
        textAlign={textAlignLeft}
        direction={textDirection}
        anchorX={anchorX}
        anchorY={anchorY}>
        {value}
      </Text>
      <Text
        ref={ref}
        renderOrder={3}
        color="#ffffff"
        font={fontFamilyNormal}
        fontSize={fontSize}
        maxWidth={0.48}
        lineHeight={1.2}
        textAlign={textAlignLeft}
        direction={textDirection}
        anchorX={anchorX}
        anchorY={anchorY}>
        {highlightValue}
      </Text>
    </group>
  );
});

const wrap = (s:string, w:number) => s.replace(
  new RegExp(`(?![^\\n]{1,${w}}$)([^\\n]{1,${w}})\\s`, 'g'), '$1\n'
);

const getLinksForSlide = (links: LinkType[], activeSlideIndex:number)=>{
  return links.filter((link )=> {
    let lowerBound = 90 * 5 * (activeSlideIndex - 1);
    let upperBound = 90 * 5 * (activeSlideIndex);
    return !(link.spaces < lowerBound || link.spaces >= upperBound);
  });
}

const DefaultText = forwardRef(({ value, highlightValue, linkValue, links,lastSlideIndex, activeSlideIndex }: TTextProps, ref) => {
  const fontSize = 0.019;
  const color = '#ffffff';
  const positionSign = LOCALE === "/ar" ? 1 : -1;
  const anchorX = positionSign === 1 ? 'right' : "left";
  const anchorY = 'top';
  const position: ReactThreeFiber.Vector3 = [positionSign * 0.24, -0.1, 0.001];
  const { gl } = useThree();

  let linksCurrentSlide = getLinksForSlide(links,activeSlideIndex);
  if(activeSlideIndex > lastSlideIndex){
    linksCurrentSlide = getLinksForSlide(links,lastSlideIndex);
  }

  return (
    <group position={position}>
      <Text
        renderOrder={2}
        color={color}
        font={fontFamilyNormal}
        fontSize={fontSize}
        maxWidth={0.48}
        lineHeight={1.2}
        textAlign={textAlignLeft}
        direction={textDirection}
        anchorX={anchorX}
        anchorY={anchorY}>
        {value}
      </Text>

      {linksCurrentSlide.map((link )=>  {
        return <><Text
          onClick={()=>{
            window.open(link.link)
          }}
          onPointerOver={()=>{
            gl.domElement.classList.add('cursor-pointer');
          }}
          onPointerOut={()=>{
            gl.domElement.classList.remove('cursor-pointer');
          }
          }
          renderOrder={4}
          color={link.color}
          font={fontFamilyNormal}
          fontSize={fontSize}
          maxWidth={0.48}
          lineHeight={1.2}
          textAlign={textAlignLeft}
          anchorX={anchorX}
          anchorY={anchorY}>
          {wrap(link.text,90)}
        </Text>
          <Text
          onClick={()=>{
            window.open(link.link)
          }}
          renderOrder={4}
          color={link.underline_color}
          font={fontFamilyNormal}
          fontSize={fontSize}
          maxWidth={0.48}
          lineHeight={1.2}
          textAlign={textAlignLeft}
          anchorX={anchorX}
          anchorY={anchorY}>
          {wrap(link.underline_text,90)}
        </Text>
        </>
        }
      )}
    </group>
  );
});

type TStoryTextProps = TTextProps & {
  isBig?: boolean;
  isTitle?: boolean;
};

const StoryText = forwardRef(({ value, highlightValue = '', linkValue = '', isTitle, isBig, links = [], lastSlideIndex, activeSlideIndex }: TStoryTextProps, ref) => {
  let Component = DefaultText;

  if (isTitle) {
    Component = TitleText;
  } else if (isBig) {
    Component = BigText;
  }

  return <Component links={links} value={value} lastSlideIndex={lastSlideIndex} highlightValue={highlightValue} activeSlideIndex={activeSlideIndex} linkValue={linkValue} ref={ref} />;
});

export default StoryText;
